/**
 * Number Column Pattern
 * 
 * Creates number columns with various formatting options including decimals, separators, and affixes.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { TABLE_SETTINGS } from '../../constants/tableSettings';

export interface NumberColumnOptions {
  header?: string;
  sortable?: boolean;
  resizable?: boolean;
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  
  // Number formatting options
  precision?: number;              // Number of decimal places (0 for integers)
  showThousandsSeparator?: boolean; // Whether to show thousands separators
  prefix?: string;                 // Text before the number (e.g., "$", "#")
  suffix?: string;                 // Text after the number (e.g., "pcs", "%", "kg")
  locale?: string;                 // Locale for number formatting
  emptyDisplay?: string;           // What to show for null/undefined values
  
  // Styling
  rightAlign?: boolean;            // Whether to right-align (default true)
  className?: string;              // Additional CSS classes
}

/**
 * Creates a number column with smart formatting and locale support
 * 
 * @param accessor - The data accessor key
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // Integer with thousands separator
 * numberColumn('quantity', { 
 *   precision: 0, 
 *   showThousandsSeparator: true,
 *   suffix: ' pcs'
 * })
 * 
 * // Float with 2 decimals
 * numberColumn('score', { 
 *   precision: 2,
 *   suffix: '%'
 * })
 * 
 * // Custom formatting
 * numberColumn('weight', {
 *   precision: 1,
 *   suffix: ' kg',
 *   prefix: '≈ '
 * })
 */
export function numberColumn<T>(
  accessor: keyof T,
  options: NumberColumnOptions = {}
): ColumnDef<T> {
  const {
    header = String(accessor),
    sortable = true,
    resizable = true,
    precision = TABLE_SETTINGS.numbers.integerPrecision,
    showThousandsSeparator = TABLE_SETTINGS.numbers.showThousandsSeparator,
    prefix = '',
    suffix = '',
    locale = TABLE_SETTINGS.numbers.locale,
    emptyDisplay = TABLE_SETTINGS.numbers.emptyDisplay,
    rightAlign = TABLE_SETTINGS.numbers.rightAlign,
    width,
    maxWidth,
    minWidth,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    meta: {
      className: rightAlign ? 'text-right' : 'text-left',
    },
    cell: ({ row }) => {
      const value = row.original[accessor] as number;
      
      // Handle null/undefined values
      if (value == null || value === undefined) {
        return emptyDisplay;
      }
      
      // Handle invalid numbers
      if (typeof value !== 'number' || isNaN(value)) {
        return emptyDisplay;
      }
      
      try {
        let formattedNumber: string;
        
        if (showThousandsSeparator) {
          // Use Intl.NumberFormat for locale-aware formatting
          formattedNumber = new Intl.NumberFormat(locale, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
          }).format(value);
        } else {
          // Simple toFixed formatting
          formattedNumber = value.toFixed(precision);
        }
        
        const result = `${prefix}${formattedNumber}${suffix}`;
        
        return result;
      } catch {
        // Fallback for invalid locale
        const fallbackNumber = value.toFixed(precision);
        return `${prefix}${fallbackNumber}${suffix}`;
      }
    },
  };
}

/**
 * Creates an integer column (no decimals) with thousands separator
 */
export function integerColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision: 0,
    showThousandsSeparator: true,
  });
}

/**
 * Creates a float column with specified decimal places
 */
export function floatColumn<T>(
  accessor: keyof T, 
  precision: number = TABLE_SETTINGS.numbers.floatPrecision,
  header?: string
): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision,
    showThousandsSeparator: TABLE_SETTINGS.numbers.showThousandsSeparator,
  });
}

/**
 * Creates a quantity column with "pcs" suffix
 */
export function quantityColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision: 0,
    showThousandsSeparator: true,
    suffix: ' pcs',
  });
}

/**
 * Creates a percentage column
 */
export function percentageColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision: 1,
    suffix: '%',
    showThousandsSeparator: false,
  });
}

/**
 * Creates a score column (e.g., ratings, test scores)
 */
export function scoreColumn<T>(
  accessor: keyof T, 
  maxScore: number = 100,
  header?: string
): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision: 1,
    suffix: ` / ${maxScore}`,
    showThousandsSeparator: false,
  });
}

/**
 * Creates a weight column with kg suffix
 */
export function weightColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision: 1,
    suffix: ' kg',
    showThousandsSeparator: false,
  });
}

/**
 * Creates an ID/index column with # prefix
 */
export function idColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return numberColumn(accessor, {
    header,
    precision: 0,
    prefix: '#',
    showThousandsSeparator: false,
  });
} 